import { Outlet, Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/category/politics-policy", label: "Politics" },
  { to: "/category/ghana", label: "Ghana" },
  { to: "/business", label: "Business" },
  { to: "/sports", label: "Sports" },
  { to: "/entertainment", label: "Entertainment" },
  { to: "/culture", label: "Culture" },
  { to: "/global-crime-watch", label: "Crime Watch" },
  { to: "/category/global", label: "Global" },
  { to: "/search", label: "Search" },
  { to: "/about", label: "About" },
];

export default function PublicLayout() {
  const [email, setEmail] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/public/newsletter/options")
      .then((r) => {
        const list = r.data.newsletters || [];
        setOptions(list);
        if (list.includes("Weekly Be-Apuo Briefing")) setSelected(["Weekly Be-Apuo Briefing"]);
      })
      .catch(() => setOptions([]));
  }, []);

  const toggle = (n) =>
    setSelected((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));

  const subscribe = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please tick the consent box to subscribe.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Choose at least one newsletter.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/newsletter/subscribe", {
        email,
        newsletters: selected,
        consent: true,
        source_page: "footer",
      });
      toast.success("Subscribed. Check your inbox for a link to manage your preferences.");
      setEmail("");
      setConsent(false);
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error("This address was unsubscribed. Please resubscribe from a confirmation link.");
      else if (status === 400) toast.error("Consent and a valid email are required.");
      else toast.error("Could not subscribe.");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="public-scope min-h-screen bg-paper text-ink font-body flex flex-col">
      <header className="sticky top-0 z-40 border-b border-ink/10 bg-paper/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" data-testid="public-logo" className="flex items-baseline gap-2">
              <span className="font-editorial font-black text-xl tracking-tight text-ink">Be-Apuo</span>
              <span className="label-caps text-ink/55">Media Intelligence</span>
            </Link>
            <nav className="hidden xl:flex items-center gap-6">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm font-medium editorial-link whitespace-nowrap ${isActive ? "text-ink" : "text-ink/60 hover:text-ink"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="nav-menu-toggle"
                aria-label="Open sections menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
                className="xl:hidden inline-flex items-center gap-1.5 text-sm font-medium text-ink border border-ink/25 px-3 py-1.5 hover:bg-ink hover:text-paper transition-colors"
              >
                {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                <span className="label-caps">Sections</span>
              </button>
              <Link
                to="/command-center/login"
                data-testid="command-center-link"
                className="hidden sm:inline-block text-xs font-semibold uppercase tracking-widest border border-ink px-3 py-2 hover:bg-ink hover:text-paper transition-colors"
              >
                Command Center
              </Link>
            </div>
          </div>
          {menuOpen && (
            <nav data-testid="nav-mobile-panel" className="xl:hidden border-t border-ink/10 py-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block py-2 text-sm font-medium ${isActive ? "text-ink" : "text-ink/60 hover:text-ink"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/command-center/login" onClick={() => setMenuOpen(false)} className="sm:hidden block py-2 text-sm font-semibold uppercase tracking-widest text-ink">Command Center</Link>
            </nav>
          )}
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-ink/10 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-12">
          <div>
            <div className="font-editorial font-black text-2xl tracking-tight text-ink">Be-Apuo</div>
            <p className="mt-3 text-sm text-ink/60 max-w-xs leading-relaxed">
              Independent research, fact-checking and analysis on Ghana, Africa, the diaspora,
              the United States and global affairs.
            </p>
          </div>
          <div>
            <div className="label-caps text-ink/55 mb-4">Sections</div>
            <ul className="space-y-2 text-sm">
              {NAV.slice(1).map((n) => (
                <li key={n.to}><Link className="text-ink hover:text-heritage editorial-link" to={n.to}>{n.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label-caps text-ink/55 mb-4">Newsletter</div>
            {done ? (
              <p data-testid="newsletter-success" className="text-sm text-ink">
                Thanks — check your inbox for a link to manage your preferences.
              </p>
            ) : (
              <form onSubmit={subscribe} className="space-y-3" data-testid="footer-newsletter-form">
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full border border-ink/20 bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-heritage"
                />
                <div className="space-y-1.5" data-testid="newsletter-options">
                  {options.map((n) => (
                    <label key={n} className="flex items-center gap-2 text-xs text-ink/75 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`newsletter-option-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        checked={selected.includes(n)}
                        onChange={() => toggle(n)}
                        className="accent-heritage"
                      />
                      <span>{n}</span>
                    </label>
                  ))}
                </div>
                <label className="flex items-start gap-2 text-xs text-ink/60 cursor-pointer">
                  <input
                    data-testid="newsletter-consent-checkbox"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 accent-heritage"
                  />
                  <span>
                    I agree to receive the selected newsletters and understand I can unsubscribe at any time.
                  </span>
                </label>
                <button
                  data-testid="newsletter-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="bg-heritage text-paper px-4 py-2 text-sm font-semibold hover:bg-heritage/90 transition-colors disabled:opacity-50"
                >
                  {submitting ? "Joining…" : "Join"}
                </button>
              </form>
            )}
          </div>
        </div>
        <div className="border-t border-ink/10 py-6 text-center text-xs text-ink/55">
          © {new Date().getFullYear()} Be-Apuo Media Intelligence. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
